// Canvas-based generator for an Instagram-Story-ready PNG that mirrors
// the in-app athlete "Liquid Glass" profile card. Output is a 1080x1920
// image the user can share straight into their Instagram Story via the
// native Web Share sheet — they then drop a Link Sticker on top to make
// it tappable. We still expose a download fallback for browsers without
// `navigator.canShare({ files })`.
//
// Visual reference: AthleteNetworkPage profile card —
//   - blurred dark hero photo background
//   - floating dark translucent card (rgba(18,18,22,0.62)) with a soft
//     white 1.5px outline (ring-white/15) and large drop shadow
//   - circular avatar (ring-white/40)
//   - uppercase Anton display name
//   - italic Libre Baskerville tagline
//   - glass action pills (Follow / Updates / Share)
//   - gradient LINK pill + host row
//   - TAP TO READ glass badge underneath

const STORY_W = 1080;
const STORY_H = 1920;

const FONT_DISPLAY = 'Anton, "Bebas Neue", Impact, system-ui, sans-serif';
const FONT_BODY    = 'Manrope, system-ui, -apple-system, sans-serif';
const FONT_ITALIC  = '"Libre Baskerville", Georgia, serif';

const loadImage = (src) => new Promise((resolve, reject) => {
  if (!src) { reject(new Error('no image src')); return; }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.referrerPolicy = 'no-referrer';
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error(`image load failed: ${src}`));
  img.src = src;
});

// Wrap text to a max width, return array of lines. Respects an optional
// max line count by truncating with an ellipsis on the last line.
const wrapLines = (ctx, text, maxWidth, maxLines = 99) => {
  if (!text) return [];
  const words = String(text).split(/\s+/);
  const lines = [];
  let line = '';
  for (const w of words) {
    const probe = line ? `${line} ${w}` : w;
    if (ctx.measureText(probe).width <= maxWidth) {
      line = probe;
    } else {
      if (line) lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  if (lines.length > maxLines) {
    const trimmed = lines.slice(0, maxLines);
    let last = trimmed[trimmed.length - 1];
    while (last && ctx.measureText(last + '…').width > maxWidth) {
      last = last.replace(/\s*\S+$/, '');
    }
    trimmed[trimmed.length - 1] = (last || '').trim() + '…';
    return trimmed;
  }
  return lines;
};

// Rounded rect path
const roundedRect = (ctx, x, y, w, h, r) => {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y,     x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x,     y + h, rr);
  ctx.arcTo(x,     y + h, x,     y,     rr);
  ctx.arcTo(x,     y,     x + w, y,     rr);
  ctx.closePath();
};

// Cover-fit image draw inside a rect (object-fit: cover)
const drawCover = (ctx, img, dx, dy, dw, dh) => {
  const ir = img.width / img.height;
  const dr = dw / dh;
  let sx = 0, sy = 0, sw = img.width, sh = img.height;
  if (ir > dr) {
    sw = img.height * dr;
    sx = (img.width - sw) / 2;
  } else {
    sh = img.width / dr;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
};

// Draw a glass "pill" with translucent fill + thin white outline.
const drawGlassPill = (ctx, x, y, w, h, { fill = 'rgba(255,255,255,0.10)', ring = 'rgba(255,255,255,0.22)', ringWidth = 2 } = {}) => {
  ctx.save();
  roundedRect(ctx, x, y, w, h, h / 2);
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.lineWidth = ringWidth;
  ctx.strokeStyle = ring;
  ctx.stroke();
  ctx.restore();
};

/**
 * Build the IG Story PNG — Liquid Glass dark edition.
 *
 * @param {{ title:string, subtitle?:string, image?:string, host?:string, brand?:string }} opts
 * @returns {Promise<{ blob: Blob, file: File, dataUrl: string }>}
 */
export async function buildStoryGraphic({ title, subtitle, image, host = 'flythouse.com', brand = 'dreamsleague' }) {
  const canvas = document.createElement('canvas');
  canvas.width = STORY_W;
  canvas.height = STORY_H;
  const ctx = canvas.getContext('2d');

  // ─── 1. Background — solid near-black + blurred dim cover photo ───
  ctx.fillStyle = '#050507';
  ctx.fillRect(0, 0, STORY_W, STORY_H);

  let cover = null;
  try { cover = await loadImage(image); } catch (e) {
    if (typeof console !== 'undefined') console.warn('[buildStoryGraphic] cover image load failed:', e?.message || e);
  }

  if (cover) {
    ctx.save();
    try { ctx.filter = 'blur(48px) brightness(0.40) saturate(1.15)'; } catch (e) {
      if (typeof console !== 'undefined') console.warn('[buildStoryGraphic] canvas filter not supported:', e?.message || e);
    }
    ctx.globalAlpha = 0.75;
    drawCover(ctx, cover, -80, -80, STORY_W + 160, STORY_H + 160);
    ctx.restore();
  }

  // Vertical darken — push dark on top + bottom for chrome contrast
  const dark = ctx.createLinearGradient(0, 0, 0, STORY_H);
  dark.addColorStop(0,    'rgba(0,0,0,0.55)');
  dark.addColorStop(0.4,  'rgba(0,0,0,0.15)');
  dark.addColorStop(0.65, 'rgba(0,0,0,0.20)');
  dark.addColorStop(1,    'rgba(0,0,0,0.75)');
  ctx.fillStyle = dark;
  ctx.fillRect(0, 0, STORY_W, STORY_H);

  // Subtle film-grain via low-alpha noise dots (keeps the dark from looking flat)
  ctx.save();
  ctx.globalAlpha = 0.04;
  ctx.fillStyle = '#ffffff';
  for (let i = 0; i < 1400; i++) {
    const x = Math.random() * STORY_W;
    const y = Math.random() * STORY_H;
    ctx.fillRect(x, y, 1.5, 1.5);
  }
  ctx.restore();

  // ─── 2. Top brand chip (glass) ───────────────────────────────────
  {
    const chipX = 70;
    const chipY = 96;
    ctx.font = `800 30px ${FONT_BODY}`;
    const handle = `@${brand}`;
    const handleW = ctx.measureText(handle).width;
    const chipPadX = 32;
    const chipH = 70;
    const dotW = 44; // small IG-style gradient dot
    const chipW = dotW + 14 + handleW + chipPadX * 2 - 18;
    drawGlassPill(ctx, chipX, chipY, chipW, chipH, {
      fill: 'rgba(255,255,255,0.08)',
      ring: 'rgba(255,255,255,0.18)',
      ringWidth: 1.5,
    });
    // gradient dot
    const dotX = chipX + 14;
    const dotY = chipY + (chipH - dotW) / 2;
    const dotG = ctx.createLinearGradient(dotX, dotY, dotX + dotW, dotY + dotW);
    dotG.addColorStop(0,   '#f9ce34');
    dotG.addColorStop(0.5, '#ee2a7b');
    dotG.addColorStop(1,   '#6228d7');
    ctx.fillStyle = dotG;
    roundedRect(ctx, dotX, dotY, dotW, dotW, dotW / 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(dotX + dotW / 2, dotY + dotW / 2, 7, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    ctx.textBaseline = 'middle';
    ctx.fillText(handle, dotX + dotW + 14, chipY + chipH / 2 + 1);
    ctx.textBaseline = 'alphabetic';
  }

  // ─── 3. Floating Liquid Glass profile card ───────────────────────
  // Mirrors AthleteNetworkPage:
  //   rounded-3xl px-7 py-7 backdrop-blur-[28px] bg-black/55
  //   ring-1 ring-white/15 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.55)]
  const cardMargin = 70;
  const cardW = STORY_W - cardMargin * 2;
  const cardX = cardMargin;
  const cardR = 56;

  // Card inner layout constants
  const padX = 60;
  const padTop = 60;
  const padBottom = 64;

  // Hero strip (rounded image at top of card)
  const heroH = 540;
  const heroR = 36;

  const avatarSize = 200;
  const avatarOverlap = 70; // how far avatar dips below the hero strip

  // Type sizes
  const labelSize = 28;       // uppercase tracking label
  const nameSize  = 108;      // Anton display
  const nameLineH = 110;
  const tagSize   = 40;       // italic tagline
  const tagLineH  = 52;

  // Measure name + tagline wrap heights
  ctx.font = `400 ${nameSize}px ${FONT_DISPLAY}`;
  const nameLines = wrapLines(ctx, (title || 'Unnamed Athlete').toUpperCase(), cardW - padX * 2, 2);
  ctx.font = `italic 400 ${tagSize}px ${FONT_ITALIC}`;
  const tagLines = subtitle ? wrapLines(ctx, subtitle, cardW - padX * 2 - 40, 3) : [];

  // Action pill row (decorative) — same labels as the live profile card
  const pillsRowH = 72;
  const linkRowH  = 92;

  const textBlockH =
    24 + labelSize +                                   // label
    16 + nameLines.length * nameLineH +                // name
    (tagLines.length ? 18 + tagLines.length * tagLineH : 0); // tagline

  const cardH =
    padTop +
    heroH +
    (avatarSize - avatarOverlap) + 14 +                // avatar dip + breathing space
    textBlockH +
    24 + pillsRowH +                                   // glass action pills
    32 + linkRowH +                                    // link footer
    padBottom;

  // Vertically center the card with a small upward bias so the brand
  // chip up top + the "TAP TO READ" badge below have room.
  const cardY = Math.max(170, Math.round((STORY_H - cardH) / 2) - 40);

  // ─── 3a. Card drop shadow + base fill ────────────────────────────
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.65)';
  ctx.shadowBlur = 80;
  ctx.shadowOffsetY = 32;
  ctx.fillStyle = 'rgba(14,14,18,0.92)'; // opaque enough to feel like a card
  roundedRect(ctx, cardX, cardY, cardW, cardH, cardR);
  ctx.fill();
  ctx.restore();

  // Liquid Glass tint layer over the card (translucent so the blurred
  // hero behind tints through) — gives the "frosted" depth feel.
  ctx.save();
  roundedRect(ctx, cardX, cardY, cardW, cardH, cardR);
  ctx.clip();
  // soft top-down highlight to mimic glass sheen
  const sheen = ctx.createLinearGradient(0, cardY, 0, cardY + cardH);
  sheen.addColorStop(0,    'rgba(255,255,255,0.06)');
  sheen.addColorStop(0.18, 'rgba(255,255,255,0.02)');
  sheen.addColorStop(1,    'rgba(0,0,0,0.18)');
  ctx.fillStyle = sheen;
  ctx.fillRect(cardX, cardY, cardW, cardH);
  ctx.restore();

  // 1.5px white-alpha border (ring-1 ring-white/15)
  ctx.save();
  ctx.lineWidth = 2;
  ctx.strokeStyle = 'rgba(255,255,255,0.16)';
  roundedRect(ctx, cardX + 1, cardY + 1, cardW - 2, cardH - 2, cardR - 1);
  ctx.stroke();
  ctx.restore();

  // ─── 3b. Hero strip with cover image ─────────────────────────────
  const heroX = cardX + padX;
  const heroY = cardY + padTop;
  const heroW = cardW - padX * 2;

  ctx.save();
  roundedRect(ctx, heroX, heroY, heroW, heroH, heroR);
  ctx.clip();
  if (cover) {
    drawCover(ctx, cover, heroX, heroY, heroW, heroH);
  } else {
    const g = ctx.createLinearGradient(heroX, heroY, heroX + heroW, heroY + heroH);
    g.addColorStop(0, '#2a1133');
    g.addColorStop(0.5, '#3a0024');
    g.addColorStop(1, '#1a0033');
    ctx.fillStyle = g;
    ctx.fillRect(heroX, heroY, heroW, heroH);
  }
  // Bottom-fade gradient INSIDE the hero so the avatar reads cleanly.
  const heroFade = ctx.createLinearGradient(0, heroY, 0, heroY + heroH);
  heroFade.addColorStop(0,    'rgba(0,0,0,0.15)');
  heroFade.addColorStop(0.55, 'rgba(0,0,0,0.20)');
  heroFade.addColorStop(1,    'rgba(10,10,14,0.85)');
  ctx.fillStyle = heroFade;
  ctx.fillRect(heroX, heroY, heroW, heroH);
  ctx.restore();

  // Tiny "DREAMS LEAGUE · NETWORK" chip floated at top-left of hero
  {
    ctx.font = `800 22px ${FONT_BODY}`;
    const txt = (brand || 'dreamsleague').toUpperCase().replace(/([A-Z]+)/g, '$1').replace('DREAMSLEAGUE', 'DREAMS LEAGUE') + '  ·  NETWORK';
    const tW = ctx.measureText(txt).width;
    const pX = heroX + 24;
    const pY = heroY + 24;
    const pW = tW + 40;
    const pH = 50;
    drawGlassPill(ctx, pX, pY, pW, pH, {
      fill: 'rgba(0,0,0,0.45)',
      ring: 'rgba(255,255,255,0.18)',
      ringWidth: 1.2,
    });
    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.textBaseline = 'middle';
    ctx.fillText(txt, pX + 20, pY + pH / 2 + 1);
    ctx.textBaseline = 'alphabetic';
  }

  // ─── 3c. Avatar circle (overlaps the hero, like a profile picture) ─
  const avatarX = cardX + cardW / 2 - avatarSize / 2;
  const avatarY = heroY + heroH - avatarOverlap;

  // ring glow under the avatar
  ctx.save();
  ctx.shadowColor = 'rgba(0,0,0,0.55)';
  ctx.shadowBlur = 30;
  ctx.shadowOffsetY = 12;
  ctx.fillStyle = '#0e0e12';
  ctx.beginPath();
  ctx.arc(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2 + 8, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // ring-2 ring-white/40
  ctx.save();
  ctx.beginPath();
  ctx.arc(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2 + 4, 0, Math.PI * 2);
  ctx.lineWidth = 5;
  ctx.strokeStyle = 'rgba(255,255,255,0.42)';
  ctx.stroke();
  ctx.restore();

  // Avatar image (clipped to circle)
  ctx.save();
  ctx.beginPath();
  ctx.arc(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, 0, Math.PI * 2);
  ctx.clip();
  if (cover) {
    drawCover(ctx, cover, avatarX, avatarY, avatarSize, avatarSize);
  } else {
    ctx.fillStyle = 'rgba(255,255,255,0.10)';
    ctx.fillRect(avatarX, avatarY, avatarSize, avatarSize);
  }
  ctx.restore();

  // ─── 3d. Text block — label / name / tagline ─────────────────────
  let cursorY = avatarY + avatarSize + 30;
  const centerX = cardX + cardW / 2;

  // Label
  ctx.fillStyle = 'rgba(170,170,180,0.95)';
  ctx.font = `700 ${labelSize}px ${FONT_BODY}`;
  ctx.textAlign = 'center';
  const labelTxt = 'DREAMS LEAGUE · NETWORK';
  // expand letter-spacing visually by drawing char-by-char
  {
    const chars = labelTxt.split('');
    const spacing = 5;
    const widths = chars.map((c) => ctx.measureText(c).width);
    const totalW = widths.reduce((a, b) => a + b, 0) + spacing * (chars.length - 1);
    let x = centerX - totalW / 2;
    for (let i = 0; i < chars.length; i++) {
      ctx.fillText(chars[i], x + widths[i] / 2, cursorY + labelSize);
      x += widths[i] + spacing;
    }
  }
  cursorY += labelSize + 16;

  // Name (Anton uppercase)
  ctx.fillStyle = '#ffffff';
  ctx.font = `400 ${nameSize}px ${FONT_DISPLAY}`;
  ctx.textAlign = 'center';
  for (const line of nameLines) {
    cursorY += nameLineH * 0.78;
    ctx.fillText(line, centerX, cursorY);
    cursorY += nameLineH * 0.22;
  }

  // Tagline (italic Libre Baskerville)
  if (tagLines.length) {
    cursorY += 22;
    ctx.fillStyle = 'rgba(220,220,230,0.85)';
    ctx.font = `italic 400 ${tagSize}px ${FONT_ITALIC}`;
    for (const line of tagLines) {
      cursorY += tagLineH * 0.78;
      ctx.fillText(line, centerX, cursorY);
      cursorY += tagLineH * 0.22;
    }
  }
  ctx.textAlign = 'left';

  // ─── 3e. Action pills row (decorative — Follow / Updates / Share) ─
  cursorY += 28;
  {
    const pills = ['Follow', 'Get Updates', 'Share'];
    ctx.font = `800 26px ${FONT_BODY}`;
    const pillPadX = 32;
    const pillH = pillsRowH;
    const pillGap = 16;
    const pillWidths = pills.map((p) => ctx.measureText(p).width + pillPadX * 2);
    const totalW = pillWidths.reduce((a, b) => a + b, 0) + pillGap * (pills.length - 1);
    let x = centerX - totalW / 2;
    pills.forEach((label, i) => {
      const pw = pillWidths[i];
      drawGlassPill(ctx, x, cursorY, pw, pillH, {
        fill: 'rgba(255,255,255,0.08)',
        ring: 'rgba(255,255,255,0.22)',
        ringWidth: 1.5,
      });
      ctx.fillStyle = 'rgba(255,255,255,0.95)';
      ctx.textBaseline = 'middle';
      ctx.textAlign = 'center';
      ctx.fillText(label, x + pw / 2, cursorY + pillH / 2 + 1);
      ctx.textBaseline = 'alphabetic';
      x += pw + pillGap;
    });
    ctx.textAlign = 'left';
    cursorY += pillH + 32;
  }

  // ─── 3f. Divider ─────────────────────────────────────────────────
  ctx.save();
  ctx.strokeStyle = 'rgba(255,255,255,0.10)';
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(heroX, cursorY);
  ctx.lineTo(heroX + heroW, cursorY);
  ctx.stroke();
  ctx.restore();
  cursorY += 28;

  // ─── 3g. Link footer — gradient pill + host right-aligned ────────
  {
    const pillW = 64;
    const pillH = 64;
    const pillX = heroX;
    const pillY = cursorY;
    const pillG = ctx.createLinearGradient(pillX, pillY, pillX + pillW, pillY + pillH);
    pillG.addColorStop(0,   '#f9ce34');
    pillG.addColorStop(0.5, '#ee2a7b');
    pillG.addColorStop(1,   '#6228d7');
    ctx.fillStyle = pillG;
    roundedRect(ctx, pillX, pillY, pillW, pillH, pillH / 2);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.beginPath();
    ctx.arc(pillX + pillW / 2, pillY + pillH / 2, 11, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(255,255,255,0.92)';
    ctx.font = `800 30px ${FONT_BODY}`;
    ctx.textBaseline = 'middle';
    ctx.fillText('LINK', pillX + pillW + 22, pillY + pillH / 2 + 1);

    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.font = `600 28px ${FONT_BODY}`;
    ctx.textAlign = 'right';
    ctx.fillText(host, heroX + heroW, pillY + pillH / 2 + 1);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
  }

  // ─── 4. Bottom "TAP TO READ" glass badge ─────────────────────────
  {
    ctx.font = `800 34px ${FONT_BODY}`;
    const badgeText = '✦  TAP TO READ';
    const badgeTextW = ctx.measureText(badgeText).width;
    const badgePadX = 42;
    const badgePadY = 26;
    const badgeW = badgeTextW + badgePadX * 2;
    const badgeH = 34 + badgePadY * 2;
    const badgeX = (STORY_W - badgeW) / 2;
    const badgeY = cardY + cardH + 70;

    // shadow
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = 26;
    ctx.shadowOffsetY = 12;
    drawGlassPill(ctx, badgeX, badgeY, badgeW, badgeH, {
      fill: 'rgba(20,20,24,0.78)',
      ring: 'rgba(255,255,255,0.18)',
      ringWidth: 1.5,
    });
    ctx.restore();

    ctx.fillStyle = '#fff';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillText(badgeText, badgeX + badgeW / 2, badgeY + badgeH / 2 + 1);
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'left';
  }

  // ─── 5. Footer watermark ─────────────────────────────────────────
  ctx.fillStyle = 'rgba(255,255,255,0.40)';
  ctx.font = `800 26px ${FONT_BODY}`;
  ctx.textAlign = 'center';
  // expand letter-spacing on the watermark
  {
    const wm = 'DREAMS LEAGUE  ·  ' + host.toUpperCase();
    const chars = wm.split('');
    const spacing = 3;
    const widths = chars.map((c) => ctx.measureText(c).width);
    const totalW = widths.reduce((a, b) => a + b, 0) + spacing * (chars.length - 1);
    let x = STORY_W / 2 - totalW / 2;
    const y = STORY_H - 86;
    for (let i = 0; i < chars.length; i++) {
      ctx.fillText(chars[i], x + widths[i] / 2, y);
      x += widths[i] + spacing;
    }
  }
  ctx.textAlign = 'left';

  // ─── Export ──────────────────────────────────────────────────────
  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => { b ? resolve(b) : reject(new Error('toBlob failed')); }, 'image/png', 0.95);
  });
  const safeTitle = (title || 'dreams-league-story')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60) || 'dreams-league-story';
  const file = new File([blob], `${safeTitle}.png`, { type: 'image/png' });
  const dataUrl = canvas.toDataURL('image/png');
  return { blob, file, dataUrl };
}
